import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Search, Plus, Edit2, Trash2, Package, Filter, ChevronDown,
  Image, DollarSign, Tag, Check, X, Barcode, Receipt, Camera,
  ChevronLeft, ChevronRight, Loader2, RefreshCw, Scan, FolderOpen, Wand2, Sparkles, Undo2, RotateCcw, Upload, FileSpreadsheet, Download, AlertCircle, Usb, Bluetooth, Eye, Edit, Lock, Unlock, Shield, AlertTriangle, Save, History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import CameraBarcodeScanner from '../components/CameraBarcodeScanner';
import PictureSearch from '../components/PictureSearch';
import useBarcodeScanner from '../hooks/useBarcodeScanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { categories } from '../data/mockData';
import { productsAPI, uploadAPI } from '../services/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ITEMS_PER_PAGE = 20;

// AI Image Refinement Options
const AI_REFINE_OPTIONS = [
  { id: 'removeBackground', label: 'Remove backgrounds', description: 'Clean professional white background' },
  { id: 'professional', label: 'Professional look', description: 'Studio-quality product photography style' },
  { id: 'enhance', label: 'Make picture look good', description: 'Enhance colors, lighting, and clarity' },
  { id: 'refineAI', label: 'Refine with AI', description: 'General AI enhancement and cleanup' },
];

const AdminProducts = ({ onBack, initialAction, onActionComplete, user }) => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState([]); // For bulk actions
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showPictureSearch, setShowPictureSearch] = useState(false);
  const [barcodeScannerMode, setBarcodeScannerMode] = useState('edit'); // 'edit' or 'add'
  const [currentPage, setCurrentPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [refiningImage, setRefiningImage] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingImageUrl, setPendingImageUrl] = useState(null);
  
  // Bulk upload state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadImages, setBulkUploadImages] = useState([]);
  const [bulkUploadCategory, setBulkUploadCategory] = useState('spices');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadUseAI, setBulkUploadUseAI] = useState(true); // Use AI to read product names
  const bulkUploadInputRef = useRef(null);
  
  // Download images state
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  
  // Hardware scanner state
  const [hardwareScannerEnabled, setHardwareScannerEnabled] = useState(true);
  const [lastHardwareScan, setLastHardwareScan] = useState(null);
  
  // Image history for reverting (stores previous image URLs)
  const [imageHistory, setImageHistory] = useState([]);
  const [originalImage, setOriginalImage] = useState(null);
  
  // Handle initial action (e.g., auto-open Add Product modal)
  useEffect(() => {
    if (initialAction?.action === 'add') {
      setShowAddModal(true);
      if (onActionComplete) {
        onActionComplete();
      }
    }
  }, [initialAction, onActionComplete]);
  
  // AI Refinement Options State
  const [showRefineOptions, setShowRefineOptions] = useState(false);
  const [selectedRefineOptions, setSelectedRefineOptions] = useState({
    removeBackground: false,
    professional: false,
    enhance: true,  // Default: just enhance the image
    refineAI: false,
  });
  
  // Duplicate Detection State
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [duplicateAction, setDuplicateAction] = useState(null); // 'edit', 'delete', 'proceed'
  
  // File input ref for product image
  const imageInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const barcodeInputRef = useRef(null);
  const imagePlaceholderRef = useRef(null);
  
  // Paste mode state
  const [pasteMode, setPasteMode] = useState(false);
  const longPressTimerRef = useRef(null);
  const [isLongPressing, setIsLongPressing] = useState(false);
  
  // CSV Import state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvErrors, setCsvErrors] = useState([]);
  
  // Delete All Products state
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Import/Restore Products state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSource, setImportSource] = useState('https://taste-of-ottawa-1f6e4ebc.base44.app');
  const [restoreFile, setRestoreFile] = useState(null);
  
  // Backup/Restore state
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [lastBackup, setLastBackup] = useState(() => {
    const saved = localStorage.getItem('lastProductBackup');
    return saved ? JSON.parse(saved) : null;
  });
  
  // AI Categorization state
  const [aiCategorizing, setAiCategorizing] = useState(false);
  const [showAiCategorizeModal, setShowAiCategorizeModal] = useState(false);
  const [aiCategorizeResult, setAiCategorizeResult] = useState(null);
  
  // Hardware barcode scanner hook
  const handleHardwareScan = useCallback((scannedBarcode) => {
    // Only process if add/edit modal is open
    if (showAddModal || editingProduct) {
      setFormData(prev => ({ ...prev, barcode: scannedBarcode }));
      setLastHardwareScan(scannedBarcode);
      toast.success(`Barcode scanned: ${scannedBarcode}`, {
        description: 'Barcode field updated',
        icon: '📟'
      });
      
      // Play success sound
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = 1000;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.1;
        oscillator.start();
        setTimeout(() => oscillator.stop(), 100);
      } catch (e) {
        // Audio context not available - ignore
      }
    }
  }, [showAddModal, editingProduct]);

  const { isScanning } = useBarcodeScanner({
    onScan: handleHardwareScan,
    enabled: hardwareScannerEnabled && (showAddModal || !!editingProduct),
    minLength: 4,
    maxDelay: 50,
    excludeInputClasses: ['no-scanner']
  });
  
  // Form state for add/edit modal
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category: 'spices',
    image: '',
    barcode: '',
    taxable: false,
    region: '🌍',
    inStock: true,
    quantity: 10,
    reorder_level: 4,
    updated: false,
    updatedDate: new Date().toISOString().split('T')[0],
    updatedBy: '',
    available: true,
  });
  
  // State for enlarged product view
  const [enlargedProduct, setEnlargedProduct] = useState(null);
  
  // State for enlarged image preview in add/edit modal
  const [enlargedImagePreview, setEnlargedImagePreview] = useState(null);

  // Fetch products from API
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await productsAPI.getAll();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (product.barcode && product.barcode.includes(searchQuery));
    
    // Special filter for products needing review
    if (selectedCategory === 'needs-review') {
      return matchesSearch && (product.needs_review || product.bulk_uploaded || product.price === 0);
    }
    
    // Support multi-category filtering
    const matchesCategory = selectedCategory === 'all' || 
      product.category === selectedCategory || 
      (product.categories && product.categories.includes(selectedCategory));
    return matchesSearch && matchesCategory;
  });

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await productsAPI.delete(productId);
      setProducts(products.filter(p => p.id !== productId));
      toast.success('Product deleted successfully');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  // Delete all products in "Needs Details" category
  const handleDeleteAllNeedsReview = async () => {
    const needsReviewProducts = products.filter(p => 
      p.needs_review || p.bulk_uploaded || p.price === 0
    );
    
    if (needsReviewProducts.length === 0) {
      toast.info('No products to delete in this category');
      return;
    }
    
    if (!window.confirm(`Delete ${needsReviewProducts.length} products that need details? This cannot be undone.`)) {
      return;
    }
    
    let deleted = 0;
    let errors = 0;
    
    for (const product of needsReviewProducts) {
      try {
        await productsAPI.delete(product.id);
        deleted++;
      } catch (error) {
        console.error(`Failed to delete ${product.name}:`, error);
        errors++;
      }
    }
    
    // Refresh products list
    fetchProducts();
    
    if (errors > 0) {
      toast.warning(`Deleted ${deleted} products. ${errors} failed.`);
    } else {
      toast.success(`Deleted ${deleted} products successfully`);
    }
  };

  // Delete ALL products from database
  const handleDeleteAllProducts = async () => {
    if (deleteConfirmText !== 'DELETE ALL') {
      toast.error('Please type "DELETE ALL" to confirm');
      return;
    }
    
    setDeletingAll(true);
    try {
      const result = await productsAPI.deleteAll();
      toast.success(result.message || `Deleted ${result.deleted_count} products`);
      setProducts([]);
      setShowDeleteAllModal(false);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Error deleting all products:', error);
      toast.error('Failed to delete all products');
    } finally {
      setDeletingAll(false);
    }
  };

  // Import products from source URL
  const handleImportFromSource = async () => {
    setImporting(true);
    try {
      // First try import from source URL (uses Playwright or backup file fallback)
      const result = await productsAPI.importFromSource(importSource, true);
      toast.success(result.message || `Imported ${result.imported_count} products`);
      fetchProducts();
      setShowImportModal(false);
    } catch (error) {
      console.error('Error importing products:', error);
      // If import fails, try loading directly from backup file
      try {
        toast.info('Trying to load from server backup file...');
        const backupResult = await productsAPI.loadFromBackup();
        toast.success(backupResult.message || `Loaded ${backupResult.imported_count} products from backup`);
        fetchProducts();
        setShowImportModal(false);
      } catch (backupError) {
        console.error('Backup load also failed:', backupError);
        toast.error('Import failed. Please use "Restore from Backup" option with a JSON file.');
      }
    } finally {
      setImporting(false);
    }
  };

  // Export products to JSON
  const handleExportProducts = async () => {
    try {
      const result = await productsAPI.exportProducts();
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_backup_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${result.product_count} products`);
    } catch (error) {
      console.error('Error exporting products:', error);
      toast.error('Failed to export products');
    }
  };

  // Restore products from JSON file
  const handleRestoreFromFile = async () => {
    if (!restoreFile) {
      toast.error('Please select a backup file');
      return;
    }
    
    setImporting(true);
    try {
      const text = await restoreFile.text();
      const data = JSON.parse(text);
      const products = data.products || data;
      
      if (!Array.isArray(products)) {
        throw new Error('Invalid backup file format');
      }
      
      const result = await productsAPI.restoreProducts(products, true);
      toast.success(result.message || `Restored ${result.imported_count} products`);
      fetchProducts();
      setShowImportModal(false);
      setRestoreFile(null);
    } catch (error) {
      console.error('Error restoring products:', error);
      toast.error(error.message || 'Failed to restore products');
    } finally {
      setImporting(false);
    }
  };

  // Backup current database to localStorage and trigger download
  const handleBackupDatabase = async () => {
    setBackingUp(true);
    try {
      const result = await productsAPI.exportProducts();
      
      // Store in localStorage for quick restore
      const backupData = {
        date: new Date().toISOString(),
        productCount: result.product_count,
        data: result
      };
      localStorage.setItem('lastProductBackup', JSON.stringify(backupData));
      setLastBackup(backupData);
      
      // Trigger download
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_backup_${new Date().toISOString().split('T')[0]}_${result.product_count}items.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Backup created: ${result.product_count} products saved`);
    } catch (error) {
      console.error('Error backing up:', error);
      toast.error('Failed to backup database');
    } finally {
      setBackingUp(false);
    }
  };

  // Restore from last backup in localStorage OR from server backup file
  const handleRestoreLastBackup = async () => {
    // Check if localStorage backup exists and has products
    if (lastBackup?.data?.products && lastBackup.productCount > 0) {
      const confirmed = window.confirm(
        `Restore ${lastBackup.productCount} products from backup created on ${new Date(lastBackup.date).toLocaleString()}?\n\nThis will replace all current products.`
      );
      
      if (!confirmed) return;
      
      setRestoring(true);
      try {
        const result = await productsAPI.restoreProducts(lastBackup.data.products, true);
        toast.success(result.message || `Restored ${result.imported_count} products`);
        fetchProducts();
      } catch (error) {
        console.error('Error restoring:', error);
        toast.error('Failed to restore from backup');
      } finally {
        setRestoring(false);
      }
    } else {
      // No valid localStorage backup - use server backup file directly
      const confirmed = window.confirm(
        'No local backup found.\n\nLoad products from server backup file (356 products)?'
      );
      
      if (!confirmed) return;
      
      setRestoring(true);
      try {
        // Try the direct backup file endpoint first (works in production)
        const result = await productsAPI.loadFromBackup();
        toast.success(result.message || `Loaded ${result.imported_count} products from server backup`);
        fetchProducts();
      } catch (error) {
        console.error('Error loading from server backup:', error);
        toast.error('Failed to load from server backup. Try using Import/Restore with a backup file.');
      } finally {
        setRestoring(false);
      }
    }
  };

  // AI Auto-Categorize products
  const handleAiCategorize = async (selectedOnly = false) => {
    setAiCategorizing(true);
    setAiCategorizeResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const productIds = selectedOnly && selectedProducts.length > 0 
        ? selectedProducts 
        : null;
      
      const response = await fetch(`${API_URL}/api/admin/ai-categorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_ids: productIds,
          batch_size: selectedOnly ? selectedProducts.length : 50
        })
      });
      
      if (!response.ok) {
        throw new Error('AI categorization failed');
      }
      
      const result = await response.json();
      setAiCategorizeResult(result);
      
      if (result.updated > 0) {
        toast.success(`AI categorized ${result.updated} products`, {
          description: `${result.processed} processed, ${result.errors} errors`
        });
        fetchProducts(); // Refresh product list
      } else if (result.errors > 0) {
        toast.error('AI categorization encountered errors');
      } else {
        toast.info('No products were updated');
      }
      
    } catch (error) {
      console.error('AI categorization error:', error);
      toast.error('Failed to run AI categorization');
    } finally {
      setAiCategorizing(false);
    }
  };

  const handleToggleStock = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    try {
      // Use snake_case for backend API
      const newStockStatus = !product.in_stock && !product.inStock; // Handle both formats
      await productsAPI.update(productId, { in_stock: newStockStatus });
      setProducts(products.map(p => 
        p.id === productId ? { ...p, in_stock: newStockStatus, inStock: newStockStatus } : p
      ));
      toast.success(newStockStatus ? 'Marked as in stock' : 'Marked as out of stock');
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error('Failed to update stock status');
    }
  };

  // Toggle image lock for a product
  const handleToggleImageLock = async (productId, lock = true) => {
    try {
      const result = await productsAPI.toggleImageLock(productId, lock);
      setProducts(products.map(p => 
        p.id === productId ? { ...p, image_locked: result.image_locked, image_locked_by: result.image_locked_by } : p
      ));
      toast.success(lock ? 'Image locked' : 'Image unlocked');
    } catch (error) {
      console.error('Error toggling image lock:', error);
      toast.error('Failed to update image lock');
    }
  };

  // Bulk upload handlers
  const handleBulkImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Filter only image files
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast.error('Please select image files');
      return;
    }
    
    // Convert files to preview URLs and prepare for upload
    const previews = imageFiles.map(file => ({
      file,
      filename: file.name,
      preview: URL.createObjectURL(file),
      uploading: false,
      uploaded: false,
      url: null,
      error: null
    }));
    
    setBulkUploadImages(previews);
    setShowBulkUploadModal(true);
    e.target.value = ''; // Reset input
  };

  const handleBulkUpload = async () => {
    if (bulkUploadImages.length === 0) return;
    
    setBulkUploading(true);
    const uploadedImages = [];
    const updatedPreviews = [...bulkUploadImages];
    
    try {
      // Upload each image
      for (let i = 0; i < updatedPreviews.length; i++) {
        const item = updatedPreviews[i];
        if (item.uploaded) {
          uploadedImages.push({ image_url: item.url, filename: item.filename });
          continue;
        }
        
        updatedPreviews[i] = { ...item, uploading: true };
        setBulkUploadImages([...updatedPreviews]);
        
        try {
          const result = await uploadAPI.uploadImage(item.file);
          updatedPreviews[i] = { ...item, uploading: false, uploaded: true, url: result.url };
          uploadedImages.push({ image_url: result.url, filename: item.filename });
        } catch (error) {
          updatedPreviews[i] = { ...item, uploading: false, error: error.message };
        }
        
        setBulkUploadImages([...updatedPreviews]);
      }
      
      if (uploadedImages.length === 0) {
        toast.error('No images were uploaded successfully');
        setBulkUploading(false);
        return;
      }
      
      // Create products from uploaded images (with AI naming if enabled)
      if (bulkUploadUseAI) {
        toast.info('🤖 AI is reading product names from images...');
      }
      const result = await productsAPI.bulkUpload(uploadedImages, bulkUploadCategory, bulkUploadUseAI);
      
      const aiMessage = result.ai_used ? ' (AI named)' : '';
      toast.success(`Created ${result.created_count} products${aiMessage}! Edit them to add prices.`);
      
      // Refresh products list
      fetchProducts();
      
      // Close modal and reset
      setShowBulkUploadModal(false);
      setBulkUploadImages([]);
      setBulkUploadCategory('spices');
      setBulkUploadUseAI(true);
      
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error(error.message || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const removeBulkImage = (index) => {
    const updated = bulkUploadImages.filter((_, i) => i !== index);
    setBulkUploadImages(updated);
    if (updated.length === 0) {
      setShowBulkUploadModal(false);
    }
  };

  // Download all product images
  const handleDownloadAllImages = async () => {
    const productsWithImages = products.filter(p => p.image && p.image.startsWith('http'));
    
    if (productsWithImages.length === 0) {
      toast.error('No product images to download');
      return;
    }
    
    // Start download immediately without confirmation
    toast.info(`Downloading ${productsWithImages.length} images...`);
    
    setDownloadingImages(true);
    setDownloadProgress({ current: 0, total: productsWithImages.length });
    
    let downloaded = 0;
    let errors = 0;
    
    // Create a delay function
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    
    for (const product of productsWithImages) {
      try {
        // Fetch the image
        const response = await fetch(product.image);
        if (!response.ok) throw new Error('Failed to fetch');
        
        const blob = await response.blob();
        
        // Get file extension from URL or content type
        let extension = 'jpg';
        const urlMatch = product.image.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
        if (urlMatch) {
          extension = urlMatch[1].toLowerCase();
        } else if (blob.type) {
          const typeMatch = blob.type.match(/image\/(\w+)/);
          if (typeMatch) extension = typeMatch[1];
        }
        
        // Create safe filename from product name
        const safeName = product.name
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .replace(/\s+/g, '_')
          .substring(0, 50);
        const filename = `${safeName}_${product.barcode || product.id.substring(0, 8)}.${extension}`;
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        downloaded++;
        setDownloadProgress({ current: downloaded, total: productsWithImages.length });
        
        // Small delay between downloads to avoid overwhelming the browser
        await delay(200);
        
      } catch (error) {
        console.error(`Failed to download image for ${product.name}:`, error);
        errors++;
      }
    }
    
    setDownloadingImages(false);
    setDownloadProgress({ current: 0, total: 0 });
    
    if (errors > 0) {
      toast.warning(`Downloaded ${downloaded} images. ${errors} failed.`);
    } else {
      toast.success(`Downloaded ${downloaded} product images!`);
    }
  };

  const openEditModal = (product) => {
    setFormData({
      name: product.name,
      price: product.price.toString(),
      category: product.category,
      image: product.image || '',
      barcode: product.barcode || '',
      taxable: product.taxable || false,
      region: product.region || '🌍',
      inStock: product.in_stock !== false && product.inStock !== false, // Handle both formats
      quantity: product.quantity || 10,
      reorder_level: product.reorder_level || 4,
      updated: product.updated || false,
      updatedDate: product.updated_date || new Date().toISOString().split('T')[0],
      updatedBy: product.updated_by || user?.name || user?.email || 'Admin',
      available: product.available !== false,
    });
    setEditingProduct(product);
    setShowRefineOptions(false);
    // Store original image for potential revert
    setOriginalImage(product.image || '');
    setImageHistory([product.image].filter(Boolean));
  };

  const openAddModal = () => {
    setFormData({
      name: '',
      price: '',
      category: 'spices',
      image: '',
      barcode: '',
      taxable: false,
      region: '🌍',
      inStock: true,
      quantity: 10,
      reorder_level: 4,
      updated: false,
      updatedDate: new Date().toISOString().split('T')[0],
      updatedBy: user?.name || user?.email || 'Admin',
      available: true,
    });
    setShowAddModal(true);
    setShowRefineOptions(false);
    setOriginalImage(null);
    setImageHistory([]);
  };

  // Handle double-click to enlarge product
  const handleProductDoubleClick = (product) => {
    setEnlargedProduct(product);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingProduct(null);
    setShowRefineOptions(false);
    setOriginalImage(null);
    setImageHistory([]);
    setPendingImageFile(null);
    setPendingImageUrl(null);
    setDuplicateWarning(null);
    setFormData({
      name: '',
      price: '',
      category: 'spices',
      image: '',
      barcode: '',
      taxable: false,
      region: '🌍',
      inStock: true,
      updated: false,
      updatedDate: new Date().toISOString().split('T')[0],
      updatedBy: '',
      available: true,
    });
  };

  // Handle file upload for product image
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      // First upload the original image
      const result = await uploadAPI.uploadImage(file);
      
      // Store the uploaded image and file for potential AI refinement
      setFormData({...formData, image: result.url});
      setPendingImageUrl(result.url);
      setPendingImageFile(file);
      
      // Store as original image (for reverting later)
      setOriginalImage(result.url);
      setImageHistory([result.url]);
      
      // Show refinement options after image upload
      setShowRefineOptions(true);
      toast.success('Image uploaded! You can enhance it with AI or use as-is.');
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  // Handle paste from clipboard
  const handleImagePaste = async (e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    const items = clipboardData?.items;
    
    if (!items) return;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if it's an image
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        
        if (!file) continue;
        
        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          toast.error('Pasted image is too large. Max 5MB allowed.');
          return;
        }
        
        setUploadingImage(true);
        setPasteMode(false);
        toast.info('Uploading pasted image...');
        
        try {
          const result = await uploadAPI.uploadImage(file);
          
          setFormData(prev => ({...prev, image: result.url}));
          setPendingImageUrl(result.url);
          setPendingImageFile(file);
          setOriginalImage(result.url);
          setImageHistory([result.url]);
          setShowRefineOptions(true);
          
          toast.success('Image pasted and uploaded successfully!');
        } catch (error) {
          console.error('Paste upload error:', error);
          toast.error('Failed to upload pasted image');
        } finally {
          setUploadingImage(false);
        }
        return;
      }
      
      // Check if it's a URL (text)
      if (item.type === 'text/plain') {
        item.getAsString((text) => {
          const trimmed = text.trim();
          // Check if it looks like an image URL
          if (trimmed.match(/^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|bmp)/i) || 
              trimmed.match(/^https?:\/\/.*\/(image|photo|img|picture)/i) ||
              trimmed.startsWith('data:image/')) {
            setFormData(prev => ({...prev, image: trimmed}));
            setOriginalImage(trimmed);
            setImageHistory([trimmed]);
            setShowRefineOptions(true);
            setPasteMode(false);
            toast.success('Image URL pasted!');
          }
        });
      }
    }
  };

  // Handle long press start (for mobile)
  const handleLongPressStart = (e) => {
    // Don't trigger on button clicks
    if (e.target.closest('button')) return;
    
    setIsLongPressing(true);
    longPressTimerRef.current = setTimeout(async () => {
      setIsLongPressing(false);
      
      // Long press completed - try to read from clipboard
      setPasteMode(true);
      
      // Focus the placeholder to receive paste events
      if (imagePlaceholderRef.current) {
        imagePlaceholderRef.current.focus();
      }
      
      // Try to read clipboard on supported browsers (mainly desktop Chrome/Edge)
      if (navigator.clipboard && navigator.clipboard.read) {
        try {
          const clipboardItems = await navigator.clipboard.read();
          for (const item of clipboardItems) {
            for (const type of item.types) {
              if (type.startsWith('image/')) {
                const blob = await item.getType(type);
                const file = new File([blob], 'pasted-image.png', { type });
                
                if (file.size > 5 * 1024 * 1024) {
                  toast.error('Image too large. Max 5MB allowed.');
                  return;
                }
                
                setUploadingImage(true);
                setPasteMode(false);
                toast.info('Uploading image from clipboard...');
                
                try {
                  const result = await uploadAPI.uploadImage(file);
                  setFormData(prev => ({...prev, image: result.url}));
                  setPendingImageUrl(result.url);
                  setOriginalImage(result.url);
                  setImageHistory([result.url]);
                  setShowRefineOptions(true);
                  toast.success('Image pasted from clipboard!');
                } catch (error) {
                  toast.error('Failed to upload image');
                } finally {
                  setUploadingImage(false);
                }
                return;
              }
            }
          }
          // No image found in clipboard
          toast.info('No image in clipboard. Copy an image first, then long-press again!', { duration: 4000 });
        } catch (error) {
          // Clipboard API not available or permission denied - common on mobile
          console.log('Clipboard read not available:', error);
          toast.info('Tap and hold complete! Now paste your image (use browser paste or Ctrl+V)', { duration: 4000 });
        }
      } else {
        // Clipboard API not available - show manual paste instructions
        toast.info('Long-press complete! Now use your device paste option or Ctrl+V', { duration: 4000 });
      }
    }, 600); // 600ms long press for better mobile UX
  };

  // Handle long press end
  const handleLongPressEnd = (e) => {
    // Don't interfere with button clicks
    if (e.target.closest('button')) return;
    
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    
    // Only reset long pressing state, not paste mode
    setIsLongPressing(false);
  };

  // Handle touch move to cancel long press if user moves finger
  const handleTouchMove = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      setIsLongPressing(false);
    }
  };

  // Activate paste mode on click
  const handlePlaceholderClick = () => {
    setPasteMode(true);
    // Focus on the placeholder to capture paste events
    if (imagePlaceholderRef.current) {
      imagePlaceholderRef.current.focus();
    }
  };

  const handlePlaceholderDoubleClick = () => {
    setPasteMode(true);
    toast.info('Ready to paste! Press Ctrl+V (or Cmd+V) to paste an image.', { duration: 3000 });
    
    // Focus on the placeholder to capture paste events
    if (imagePlaceholderRef.current) {
      imagePlaceholderRef.current.focus();
    }
  };

  // Toggle refinement option
  const toggleRefineOption = (optionId) => {
    setSelectedRefineOptions(prev => ({
      ...prev,
      [optionId]: !prev[optionId]
    }));
  };

  // AI refinement with selected options
  const handleRefineImage = async () => {
    const imageUrl = formData.image || pendingImageUrl;
    if (!imageUrl) {
      toast.error('No image to refine');
      return;
    }

    // Check if any option is selected
    const hasSelection = Object.values(selectedRefineOptions).some(v => v);
    if (!hasSelection) {
      toast.error('Please select at least one refinement option');
      return;
    }

    setRefiningImage(true);
    toast.info('AI is enhancing your image...');

    try {
      // Fetch the image and convert to base64
      let imageBase64;
      
      if (pendingImageFile) {
        // Use the file directly
        imageBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(pendingImageFile);
        });
      } else {
        // Fetch from URL
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        imageBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(blob);
        });
      }

      // Call AI refinement with selected options
      const refined = await uploadAPI.refineImage(imageBase64, 'both', {
        background: selectedRefineOptions.removeBackground,
        enhance: selectedRefineOptions.enhance || selectedRefineOptions.refineAI,
        professional: selectedRefineOptions.professional
      });

      if (refined.original_preserved) {
        toast.warning('Could not refine image, keeping original');
      } else {
        // Store current image in history before replacing
        const currentImage = formData.image;
        if (currentImage && !imageHistory.includes(currentImage)) {
          setImageHistory(prev => [...prev, currentImage]);
        }
        
        // Upload the refined image
        const blob = await fetch(refined.refined_image_base64).then(r => r.blob());
        const refinedFile = new File([blob], 'refined_product.png', { type: 'image/png' });
        const uploadResult = await uploadAPI.uploadImage(refinedFile);
        
        // Update form with refined image
        setFormData({...formData, image: uploadResult.url});
        
        // Add refined image to history
        setImageHistory(prev => [...prev, uploadResult.url]);
        
        // Build success message based on selected options
        const appliedOptions = [];
        if (selectedRefineOptions.removeBackground) appliedOptions.push('white background');
        if (selectedRefineOptions.professional) appliedOptions.push('professional look');
        if (selectedRefineOptions.enhance) appliedOptions.push('enhanced colors');
        if (selectedRefineOptions.refineAI) appliedOptions.push('AI refinement');
        
        toast.success(`Image enhanced! Applied: ${appliedOptions.join(', ')}. Original preserved - you can revert anytime.`);
      }
    } catch (error) {
      console.error('Refinement error:', error);
      toast.error('Image enhancement failed');
    } finally {
      setRefiningImage(false);
    }
  };
  
  // Revert to previous image
  const revertToPreviousImage = () => {
    if (imageHistory.length > 1) {
      // Remove current image from history
      const newHistory = [...imageHistory];
      newHistory.pop();
      const previousImage = newHistory[newHistory.length - 1];
      
      setFormData({...formData, image: previousImage});
      setImageHistory(newHistory);
      toast.success('Reverted to previous image');
    } else if (originalImage && formData.image !== originalImage) {
      // Revert to original
      setFormData({...formData, image: originalImage});
      setImageHistory([originalImage]);
      toast.success('Reverted to original image');
    } else {
      toast.info('No previous image to revert to');
    }
  };
  
  // Revert to original image
  const revertToOriginal = () => {
    if (originalImage) {
      setFormData({...formData, image: originalImage});
      setImageHistory([originalImage]);
      toast.success('Reverted to original uploaded image');
    } else {
      toast.info('No original image available');
    }
  };

  // CSV Import Functions
  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          toast.error('CSV file is empty or has no data rows');
          return;
        }
        
        // Parse header
        const header = lines[0].split(',').map(h => h.trim().toLowerCase());
        const requiredFields = ['name', 'price'];
        const missingFields = requiredFields.filter(f => !header.includes(f));
        
        if (missingFields.length > 0) {
          toast.error(`Missing required columns: ${missingFields.join(', ')}`);
          return;
        }
        
        // Parse data rows
        const products = [];
        const errors = [];
        
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length !== header.length) {
            errors.push(`Row ${i + 1}: Column count mismatch`);
            continue;
          }
          
          const product = {};
          header.forEach((col, idx) => {
            product[col] = values[idx]?.trim() || '';
          });
          
          // Validate
          if (!product.name) {
            errors.push(`Row ${i + 1}: Missing product name`);
            continue;
          }
          
          const price = parseFloat(product.price);
          if (isNaN(price) || price < 0) {
            errors.push(`Row ${i + 1}: Invalid price "${product.price}"`);
            continue;
          }
          
          products.push({
            name: product.name,
            price: price,
            category: product.category || 'spices',
            image: product.image || 'https://via.placeholder.com/200',
            barcode: product.barcode || '',
            taxable: product.taxable?.toLowerCase() === 'true' || product.taxable === '1',
            region: product.region || '🌍',
            inStock: product.instock?.toLowerCase() !== 'false' && product.instock !== '0'
          });
        }
        
        setCsvPreview(products);
        setCsvErrors(errors);
        setShowCsvModal(true);
        
      } catch (error) {
        console.error('CSV parse error:', error);
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };
  
  // Parse CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };
  
  const importCsvProducts = async () => {
    if (!csvPreview || csvPreview.length === 0) {
      toast.error('No products to import');
      return;
    }
    
    setCsvUploading(true);
    let imported = 0;
    let failed = 0;
    
    try {
      for (const product of csvPreview) {
        try {
          await productsAPI.create(product);
          imported++;
        } catch (error) {
          console.error('Failed to import product:', product.name, error);
          failed++;
        }
      }
      
      await fetchProducts();
      setShowCsvModal(false);
      setCsvPreview(null);
      setCsvErrors([]);
      
      if (failed === 0) {
        toast.success(`Successfully imported ${imported} products!`);
      } else {
        toast.warning(`Imported ${imported} products, ${failed} failed`);
      }
      
    } catch (error) {
      console.error('CSV import error:', error);
      toast.error('Failed to import products');
    } finally {
      setCsvUploading(false);
    }
  };

  // Download sample CSV template
  const downloadSampleCsv = (type) => {
    let csvContent = '';
    let filename = '';
    
    if (type === 'basic') {
      // Basic template with required fields only
      csvContent = `name,price,category
Jollof Rice Mix 500g,8.99,grains
Palm Oil 1L,12.99,oils
Egusi Seeds 250g,6.49,spices
Plantain Chips 150g,3.99,snacks
Ginger Beer 330ml,2.49,beverages`;
      filename = 'products_basic_template.csv';
    } else if (type === 'full') {
      // Full template with all fields
      csvContent = `name,price,category,barcode,taxable,inStock,region,image
Jollof Rice Mix 500g,8.99,grains,6939001234567,false,true,🇳🇬,https://via.placeholder.com/200
Palm Oil 1L,12.99,oils,6939001234568,false,true,🌍,https://via.placeholder.com/200
Egusi Seeds 250g,6.49,spices,6939001234569,false,true,🇳🇬,https://via.placeholder.com/200
Plantain Chips 150g,3.99,snacks,6939001234570,true,true,🇬🇭,https://via.placeholder.com/200
Ginger Beer 330ml,2.49,beverages,6939001234571,true,true,🇯🇲,https://via.placeholder.com/200`;
      filename = 'products_full_template.csv';
    } else if (type === 'categories') {
      // Category reference
      csvContent = `Available Categories for Import
spices - Spices & Seasonings
grains - Grains & Rice
meats - Meats
seafood - Seafood
produce - Fresh Produce
beverages - Beverages
snacks - Snacks
canned - Canned Goods
frozen - Frozen Foods
sauces - Sauces
dairy - Dairy
oils - Oils

Available Regions
🌍 - Global
🇳🇬 - Nigerian
🇬🇭 - Ghanaian
🇯🇲 - Jamaican
🇹🇹 - Trinidadian
🇪🇹 - Ethiopian`;
      filename = 'category_reference.txt';
    }
    
    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${filename}`);
  };

  // Open file picker
  const openImagePicker = (captureType = null) => {
    if (imageInputRef.current) {
      if (captureType) {
        imageInputRef.current.setAttribute('capture', captureType);
      } else {
        imageInputRef.current.removeAttribute('capture');
      }
      imageInputRef.current.click();
    }
  };

  // Check for duplicate products
  const checkForDuplicates = async (name, image, excludeId = null) => {
    if (!name || name.length < 3) {
      setDuplicateWarning(null);
      return;
    }
    
    setCheckingDuplicates(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/products/check-duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          image: image || null,
          exclude_id: excludeId
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.has_duplicates) {
          setDuplicateWarning(data);
        } else {
          setDuplicateWarning(null);
        }
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
    } finally {
      setCheckingDuplicates(false);
    }
  };

  // Debounced duplicate check when name changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if ((showAddModal || editingProduct) && formData.name) {
        checkForDuplicates(formData.name, formData.image, editingProduct?.id);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [formData.name, formData.image, showAddModal, editingProduct]);

  // Fetch products on mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const handleSaveProduct = async () => {
    if (!formData.name || !formData.price) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      // Map frontend camelCase to backend snake_case
      const productData = {
        name: formData.name,
        price: parseFloat(formData.price),
        category: formData.category,
        image: formData.image || null, // Use null instead of placeholder - let backend handle default
        barcode: formData.barcode || `6939${Date.now().toString().slice(-9)}`,
        taxable: formData.taxable,
        region: formData.region,
        in_stock: formData.inStock, // snake_case for backend
        quantity: formData.quantity || 10,
        reorder_level: formData.reorder_level || 4, // snake_case for backend
        updated: formData.updated,
        updated_date: formData.updatedDate,
        updated_by: formData.updatedBy || user?.name || user?.email || 'Admin',
        available: formData.available,
      };

      if (editingProduct) {
        // Update existing product - preserve the original image if not changed
        if (!productData.image && editingProduct.image) {
          productData.image = editingProduct.image;
        }
        const updated = await productsAPI.update(editingProduct.id, productData);
        // Map backend response back to frontend format
        setProducts(products.map(p => p.id === editingProduct.id ? { 
          ...p, 
          ...productData,
          inStock: productData.in_stock, // Map back to camelCase for frontend
        } : p));
        toast.success('Product updated successfully');
      } else {
        // Add new product
        const newProduct = await productsAPI.create(productData);
        setProducts([{ ...newProduct, inStock: newProduct.in_stock }, ...products]);
        toast.success('Product added successfully');
      }
      closeModal();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-gray-800">Product Management</h1>
          <p className="text-sm sm:text-base text-gray-500">Manage inventory ({products.length} products)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline"
            onClick={fetchProducts}
            size="sm"
            className="flex items-center gap-1 sm:gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <input
            type="file"
            ref={csvInputRef}
            accept=".csv"
            onChange={handleCsvUpload}
            className="hidden"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-1 sm:gap-2">
                <FileSpreadsheet className="w-4 h-4" />
                <span className="hidden sm:inline">Import CSV</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => csvInputRef.current?.click()} className="cursor-pointer">
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV File
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => downloadSampleCsv('basic')} className="cursor-pointer">
                <Download className="w-4 h-4 mr-2" />
                Basic Template (name, price, category)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadSampleCsv('full')} className="cursor-pointer">
                <Download className="w-4 h-4 mr-2" />
                Full Template (all fields)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadSampleCsv('categories')} className="cursor-pointer">
                <Download className="w-4 h-4 mr-2" />
                Category Reference Guide
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={async () => {
                  if (!confirm('Lock all product images? This will prevent accidental changes to images.')) return;
                  try {
                    const result = await productsAPI.lockAllImages();
                    toast.success(result.message);
                    fetchProducts();
                  } catch (error) {
                    toast.error('Failed to lock images');
                  }
                }} 
                className="cursor-pointer text-blue-600"
              >
                <Shield className="w-4 h-4 mr-2" />
                Lock All Product Images
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            type="file"
            ref={bulkUploadInputRef}
            accept="image/*"
            multiple
            onChange={handleBulkImageSelect}
            className="hidden"
          />
          <Button 
            onClick={handleDownloadAllImages}
            disabled={downloadingImages}
            size="sm"
            variant="outline"
            className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300 flex items-center gap-1 sm:gap-2"
          >
            <Download className="w-4 h-4" />
            {downloadingImages ? (
              <span className="hidden sm:inline">
                {downloadProgress.current}/{downloadProgress.total}
              </span>
            ) : (
              <>
                <span className="hidden sm:inline">Download Images</span>
                <span className="sm:hidden">Images</span>
              </>
            )}
          </Button>
          <Button 
            onClick={() => bulkUploadInputRef.current?.click()}
            size="sm"
            variant="outline"
            className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300 flex items-center gap-1 sm:gap-2"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Bulk Upload</span>
            <span className="sm:hidden">Bulk</span>
          </Button>
          <Button 
            onClick={openAddModal}
            size="sm"
            className="bg-green-500 hover:bg-green-600 text-white flex items-center gap-1 sm:gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Product</span>
            <span className="sm:hidden">Add</span>
          </Button>
          <Button 
            onClick={() => setShowImportModal(true)}
            size="sm"
            variant="outline"
            className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300 flex items-center gap-1 sm:gap-2"
            data-testid="import-products-btn"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Import/Restore</span>
            <span className="sm:hidden">Import</span>
          </Button>
          <Button 
            onClick={handleBackupDatabase}
            size="sm"
            variant="outline"
            disabled={backingUp}
            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-300 flex items-center gap-1 sm:gap-2"
            data-testid="backup-database-btn"
          >
            {backingUp ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Backup
          </Button>
          <Button 
            onClick={handleRestoreLastBackup}
            size="sm"
            variant="outline"
            disabled={restoring}
            className="bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-300 flex items-center gap-1 sm:gap-2"
            data-testid="restore-backup-btn"
            title={lastBackup && lastBackup.productCount > 0 ? `Last backup: ${new Date(lastBackup.date).toLocaleString()} (${lastBackup.productCount} products)` : 'Restore from server backup (356 products)'}
          >
            {restoring ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <History className="w-4 h-4" />
            )}
            Restore
          </Button>
          <Button 
            onClick={() => handleAiCategorize(selectedProducts.length > 0)}
            size="sm"
            variant="outline"
            disabled={aiCategorizing}
            className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300 flex items-center gap-1 sm:gap-2"
            data-testid="ai-categorize-btn"
            title={selectedProducts.length > 0 ? `AI categorize ${selectedProducts.length} selected products` : 'AI categorize all products (batch of 50)'}
          >
            {aiCategorizing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">
              {selectedProducts.length > 0 ? `AI Cat (${selectedProducts.length})` : 'AI Categorize'}
            </span>
            <span className="sm:hidden">AI</span>
          </Button>
          <Button 
            onClick={() => setShowDeleteAllModal(true)}
            size="sm"
            variant="destructive"
            className="flex items-center gap-1 sm:gap-2"
            data-testid="delete-all-products-btn"
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">Delete All</span>
            <span className="sm:hidden">Clear</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 text-sm"
              />
            </div>
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="needs-review" className="text-orange-600">
                ⚠️ Needs Details
              </SelectItem>
              {categories.filter(c => c.id !== 'all').map(category => (
                <SelectItem key={category.id} value={category.id}>
                  {category.emoji} {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Delete All button for Needs Details category */}
          {selectedCategory === 'needs-review' && filteredProducts.length > 0 && (
            <Button
              onClick={handleDeleteAllNeedsReview}
              size="sm"
              variant="destructive"
              className="flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">Delete All ({filteredProducts.length})</span>
              <span className="sm:hidden">Delete ({filteredProducts.length})</span>
            </Button>
          )}
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={() => {
                setBarcodeScannerMode('edit');
                setShowBarcodeScanner(true);
              }}
              size="sm"
              className="flex-1 sm:flex-none bg-purple-500 hover:bg-purple-600 text-white text-xs sm:text-sm"
              data-testid="scan-to-edit-btn"
            >
              <Camera className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Scan to Edit</span>
            </Button>
            <Button
              onClick={() => setShowPictureSearch(true)}
              size="sm"
              className="flex-1 sm:flex-none bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-xs sm:text-sm"
              data-testid="picture-search-btn"
            >
              <Sparkles className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Picture Search</span>
            </Button>
          </div>
        </div>
      </Card>

      {/* Products Grid - 2 per row with bigger pictures */}
      <Card className="p-4 sm:p-6">
        <div className="grid grid-cols-2 gap-4 sm:gap-6">
          {paginatedProducts.map((product, index) => {
            const quantity = product.quantity ?? 10;
            const isOutOfStock = quantity === 0;
            const needsReorder = quantity > 0 && quantity <= 4;
            const isInStock = quantity > 4;
            
            // Get stock status
            const getStockStatus = () => {
              if (isOutOfStock) return { label: 'Out of Stock', bg: 'bg-red-500', border: 'border-red-400' };
              if (needsReorder) return { label: 'Re-order', bg: 'bg-orange-500', border: 'border-orange-400' };
              return { label: 'In Stock', bg: 'bg-green-500', border: 'border-green-400' };
            };
            const status = getStockStatus();
            
            return (
              <div 
                key={product.id} 
                className={`bg-white rounded-2xl border-2 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-[1.02] product-card ${status.border}`}
                style={{ animationDelay: `${index * 0.05}s` }}
                onDoubleClick={() => handleProductDoubleClick(product)}
                title="Double-click to enlarge"
              >
                {/* Large Product Image */}
                <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-blue-50">
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/300?text=No+Image';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <span className="text-gray-400 text-sm">No Image</span>
                    </div>
                  )}
                  
                  {/* Stock Status Badge */}
                  <div className={`absolute top-3 left-3 px-3 py-1.5 rounded-full ${status.bg} text-white text-xs font-bold shadow-lg`}>
                    {status.label}
                  </div>
                  
                  {/* Quantity Badge */}
                  <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold shadow-lg ${
                    isOutOfStock ? 'bg-red-100 text-red-700' : needsReorder ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                  }`}>
                    Qty: {quantity}
                  </div>
                  
                  {/* UPDATED Badge */}
                  {product.updated && (
                    <div className="absolute bottom-3 left-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg animate-pulse">
                      <RefreshCw className="w-3.5 h-3.5" />
                      UPDATED
                    </div>
                  )}
                  
                  {/* Needs Review Badge (for bulk uploaded products) */}
                  {(product.needs_review || product.bulk_uploaded) && product.price === 0 && (
                    <div className="absolute bottom-3 left-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg animate-pulse">
                      <AlertCircle className="w-3.5 h-3.5" />
                      NEEDS DETAILS
                    </div>
                  )}
                  
                  {/* Image Lock Indicator */}
                  {product.image_locked && (
                    <div 
                      className="absolute bottom-3 right-3 bg-blue-600 text-white p-1.5 rounded-full shadow-lg cursor-pointer hover:bg-blue-700 transition-colors"
                      title={`Image locked by ${product.image_locked_by || 'Admin'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleImageLock(product.id, false);
                      }}
                    >
                      <Lock className="w-3.5 h-3.5" />
                    </div>
                  )}
                  
                  {/* Out of Stock Overlay */}
                  {isOutOfStock && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <span className="text-white text-lg font-bold bg-red-500 px-4 py-2 rounded-full">OUT OF STOCK</span>
                    </div>
                  )}
                </div>
                
                {/* Product Info */}
                <div className="p-4">
                  <p className="text-base font-bold text-gray-800 line-clamp-2 min-h-[48px]" title={product.name}>
                    {product.name}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 flex-wrap">
                    {/* Show multiple categories if available */}
                    {product.categories && product.categories.length > 0 ? (
                      product.categories.map((cat, idx) => (
                        <span key={cat} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-full text-xs">
                          <span>{categories.find(c => c.id === cat)?.emoji || '📦'}</span>
                          <span>{cat}</span>
                        </span>
                      ))
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <span>{categories.find(c => c.id === product.category)?.emoji}</span>
                        <span>{product.category}</span>
                      </span>
                    )}
                    {product.barcode && (
                      <>
                        <span>•</span>
                        <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{product.barcode}</code>
                      </>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-2xl font-extrabold text-green-600">${product.price.toFixed(2)}</span>
                    <div className="flex gap-2">
                      <button 
                        onClick={(e) => { e.stopPropagation(); openEditModal(product); }}
                        className="w-10 h-10 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center transition-colors shadow-md"
                      >
                        <Edit2 className="w-5 h-5 text-blue-600" />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteProduct(product.id); }}
                        className="w-10 h-10 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center transition-colors shadow-md"
                      >
                        <Trash2 className="w-5 h-5 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredProducts.length)} of {filteredProducts.length} products
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className={currentPage === pageNum ? "bg-blue-500" : ""}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {filteredProducts.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No products found</p>
          </div>
        )}
      </Card>

      {/* Add/Edit Product Modal */}
      {(showAddModal || editingProduct) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <Card className="relative z-10 w-full max-w-lg mx-2 sm:mx-4 p-4 sm:p-6 max-h-[95vh] overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</Label>
                <Input 
                  placeholder="Enter product name" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="text-sm"
                />
                {checkingDuplicates && (
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Checking for similar products...
                  </p>
                )}
              </div>
              
              {/* Duplicate Warning Banner */}
              {duplicateWarning && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-2 sm:p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-amber-800">
                        Potential duplicates detected!
                      </p>
                      {duplicateWarning.name_matches?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-amber-700 font-medium">Similar names:</p>
                          <div className="mt-1 space-y-1">
                            {duplicateWarning.name_matches.slice(0, 3).map((match, idx) => (
                              <div key={idx} className="flex items-center gap-1 sm:gap-2 text-xs bg-white/50 rounded px-2 py-1">
                                {match.product.image && (
                                  <img src={match.product.image} alt="" className="w-5 h-5 sm:w-6 sm:h-6 rounded object-cover flex-shrink-0" />
                                )}
                                <span className="font-medium text-gray-700 flex-1 truncate">{match.product.name}</span>
                                <span className="text-amber-600 text-xs whitespace-nowrap">({match.similarity}%)</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-1 sm:px-2 text-blue-600 hover:bg-blue-50"
                                  onClick={() => {
                                    setSelectedDuplicate(match.product);
                                    setShowDuplicateModal(true);
                                  }}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  Review
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {duplicateWarning.image_matches?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-amber-700 font-medium">Same image used by:</p>
                          <div className="mt-1 space-y-1">
                            {duplicateWarning.image_matches.slice(0, 2).map((match, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs bg-white/50 rounded px-2 py-1">
                                {match.product.image && (
                                  <img src={match.product.image} alt="" className="w-6 h-6 rounded object-cover" />
                                )}
                                <span className="font-medium text-gray-700 flex-1">{match.product.name}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-blue-600 hover:bg-blue-50"
                                  onClick={() => {
                                    setSelectedDuplicate(match.product);
                                    setShowDuplicateModal(true);
                                  }}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  Review
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 text-amber-700 border-amber-300 hover:bg-amber-100"
                          onClick={() => {
                            // Show all duplicates in modal for comparison
                            const allDuplicates = [
                              ...(duplicateWarning.name_matches || []),
                              ...(duplicateWarning.image_matches || [])
                            ];
                            if (allDuplicates.length > 0) {
                              setSelectedDuplicate(allDuplicates[0].product);
                              setShowDuplicateModal(true);
                            }
                          }}
                        >
                          <Search className="w-3 h-3 mr-1" />
                          Review All Duplicates
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="text-xs h-7 bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => setDuplicateWarning(null)}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Proceed Anyway
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-1">Price *</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    placeholder="0.00" 
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-1">Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({...formData, category: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.filter(c => c.id !== 'all').map(category => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.emoji} {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-1">
                  <Image className="w-4 h-4 inline mr-1" />
                  Product Image
                </Label>
                
                {/* Image Placeholder - Click/Touch to focus, then paste */}
                <div
                  ref={imagePlaceholderRef}
                  tabIndex={0}
                  onClick={handlePlaceholderClick}
                  onDoubleClick={handlePlaceholderDoubleClick}
                  onPaste={handleImagePaste}
                  onBlur={() => !isLongPressing && setPasteMode(false)}
                  onTouchStart={handleLongPressStart}
                  onTouchEnd={handleLongPressEnd}
                  onTouchMove={handleTouchMove}
                  onTouchCancel={handleLongPressEnd}
                  onContextMenu={(e) => {
                    // Allow context menu on mobile for paste option
                    if (pasteMode) {
                      // Don't prevent - let user use native paste
                    }
                  }}
                  className={`relative w-full h-48 mb-3 rounded-xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center overflow-hidden select-none touch-manipulation ${
                    isLongPressing
                      ? 'border-blue-500 bg-blue-50 scale-[0.98] shadow-inner'
                      : pasteMode 
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-300' 
                        : formData.image 
                          ? 'border-green-400 bg-green-50' 
                          : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50 active:bg-blue-100'
                  }`}
                  data-testid="image-paste-placeholder"
                >
                  {uploadingImage ? (
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-600">Uploading image...</p>
                    </div>
                  ) : formData.image ? (
                    <>
                      <img 
                        src={formData.image} 
                        alt="Product Preview" 
                        className="absolute inset-0 w-full h-full object-contain p-2"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 hover:bg-black/40 active:bg-black/50 transition-all flex items-center justify-center opacity-0 hover:opacity-100 active:opacity-100">
                        <div className="text-center text-white">
                          <p className="text-sm font-semibold">Tap & hold to replace</p>
                          <p className="text-xs mt-1">or Ctrl+V / Cmd+V</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({...formData, image: ''});
                          setShowRefineOptions(false);
                          setOriginalImage(null);
                          setImageHistory([]);
                          setPasteMode(false);
                        }}
                        className="absolute top-2 right-2 w-8 h-8 p-0 rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : isLongPressing ? (
                    <div className="text-center">
                      <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center relative">
                        <Upload className="w-10 h-10 text-blue-600" />
                        <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
                      </div>
                      <p className="text-base font-bold text-blue-700">Keep holding...</p>
                      <p className="text-sm text-blue-600 mt-1">Release to paste</p>
                    </div>
                  ) : pasteMode ? (
                    <div className="text-center">
                      <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-purple-100 flex items-center justify-center animate-pulse">
                        <Upload className="w-10 h-10 text-purple-600" />
                      </div>
                      <p className="text-base font-bold text-purple-700">Ready to Paste!</p>
                      <p className="text-sm text-purple-600 mt-1">Use Ctrl+V, Cmd+V, or device paste</p>
                      <p className="text-xs text-gray-500 mt-2">Tap outside to cancel</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                        <Image className="w-10 h-10 text-gray-400" />
                      </div>
                      <p className="text-base font-bold text-gray-700">Paste Image Here</p>
                      <p className="text-sm text-gray-500 mt-1">
                        <span className="hidden sm:inline">Ctrl+V / Cmd+V</span>
                        <span className="sm:hidden">Tap & hold to paste</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-2">Or use buttons below</p>
                    </div>
                  )}
                </div>
                
                {/* Upload buttons */}
                <div className="flex gap-2 flex-wrap mb-2">
                  <input
                    type="file"
                    ref={imageInputRef}
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openImagePicker('environment')}
                    disabled={uploadingImage}
                    className="flex items-center gap-1"
                    data-testid="product-camera-btn"
                  >
                    {uploadingImage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                    Camera
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openImagePicker()}
                    disabled={uploadingImage}
                    className="flex items-center gap-1"
                    data-testid="product-gallery-btn"
                  >
                    {uploadingImage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FolderOpen className="w-4 h-4" />
                    )}
                    Gallery
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.open('https://ai-photo-enhancer-5106d3cf.base44.app', '_blank', 'width=800,height=600');
                      toast.info('AI Photo Enhancer opened! After enhancing your image, copy the URL and paste it below.', {
                        duration: 5000,
                      });
                    }}
                    className="flex items-center gap-1 text-purple-600 border-purple-300 hover:bg-purple-50"
                    data-testid="ai-enhancer-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                    AI Enhancer
                  </Button>
                </div>

                {/* URL input for AI Enhanced images or direct URLs */}
                <div className="relative">
                  <Input 
                    placeholder="Or paste image URL here..." 
                    value={formData.image}
                    onChange={(e) => {
                      setFormData({...formData, image: e.target.value});
                      if (e.target.value) {
                        setShowRefineOptions(true);
                        setOriginalImage(e.target.value);
                        setImageHistory([e.target.value]);
                      }
                    }}
                    className="pr-10"
                    data-testid="product-image-url-input"
                  />
                  {formData.image && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFormData({...formData, image: ''});
                        setShowRefineOptions(false);
                        setOriginalImage(null);
                        setImageHistory([]);
                      }}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 p-1 h-auto"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                {formData.image && (
                  <div className="mt-3">
                    {/* Image Lock Status */}
                    {editingProduct && (
                      <div className={`flex items-center justify-between p-2 rounded-lg mb-3 ${
                        editingProduct.image_locked ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          {editingProduct.image_locked ? (
                            <Lock className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Unlock className="w-4 h-4 text-gray-500" />
                          )}
                          <span className={`text-sm ${editingProduct.image_locked ? 'text-blue-700 font-medium' : 'text-gray-600'}`}>
                            {editingProduct.image_locked 
                              ? `Image locked by ${editingProduct.image_locked_by || 'Admin'}` 
                              : 'Image not locked'}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleImageLock(editingProduct.id, !editingProduct.image_locked)}
                          className={editingProduct.image_locked ? 'text-blue-600 hover:bg-blue-100' : 'text-gray-600 hover:bg-gray-100'}
                        >
                          {editingProduct.image_locked ? (
                            <>
                              <Unlock className="w-3 h-3 mr-1" />
                              Unlock
                            </>
                          ) : (
                            <>
                              <Lock className="w-3 h-3 mr-1" />
                              Lock
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    
                    {/* AI Refinement Toggle */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRefineOptions(!showRefineOptions)}
                      className="text-purple-600 hover:bg-purple-50 mb-3"
                    >
                      <Wand2 className="w-4 h-4 mr-1" />
                      {showRefineOptions ? 'Hide AI Options' : 'Enhance with AI'}
                    </Button>
                    
                    {/* AI Refinement Options Panel */}
                    {showRefineOptions && (
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                        <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                          <Sparkles className="w-4 h-4" />
                          AI Image Enhancement Options
                        </h4>
                        <p className="text-xs text-purple-600 mb-3">Select one or more options to enhance your product image</p>
                        
                        <div className="space-y-3">
                          {AI_REFINE_OPTIONS.map(option => (
                            <div 
                              key={option.id}
                              className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                                selectedRefineOptions[option.id] 
                                  ? 'bg-purple-100 border border-purple-300' 
                                  : 'bg-white/50 border border-transparent hover:border-purple-200'
                              }`}
                              onClick={() => toggleRefineOption(option.id)}
                            >
                              <Checkbox
                                id={option.id}
                                checked={selectedRefineOptions[option.id]}
                                onCheckedChange={() => toggleRefineOption(option.id)}
                                className="mt-0.5"
                              />
                              <div className="flex-1">
                                <label 
                                  htmlFor={option.id} 
                                  className="text-sm font-medium text-gray-800 cursor-pointer"
                                >
                                  {option.label}
                                </label>
                                <p className="text-xs text-gray-500">{option.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <Button
                          type="button"
                          onClick={handleRefineImage}
                          disabled={refiningImage || !Object.values(selectedRefineOptions).some(v => v)}
                          className="w-full mt-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                          data-testid="perform-ai-task-btn"
                        >
                          {refiningImage ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Wand2 className="w-4 h-4 mr-2" />
                              Perform selected task
                            </>
                          )}
                        </Button>
                        
                        {/* Revert Buttons */}
                        {imageHistory.length > 1 && (
                          <div className="flex gap-2 mt-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={revertToPreviousImage}
                              className="flex-1 text-orange-600 hover:bg-orange-50"
                            >
                              <Undo2 className="w-4 h-4 mr-1" />
                              Undo Last
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={revertToOriginal}
                              className="flex-1 text-blue-600 hover:bg-blue-50"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Original
                            </Button>
                          </div>
                        )}
                        
                        <p className="text-[10px] text-purple-500 mt-2 text-center">
                          Your original image is preserved. You can always revert.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-2">
                  <Barcode className="w-4 h-4 inline mr-1" />
                  Barcode
                </Label>
                
                {/* Scanner Mode Toggle */}
                <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setHardwareScannerEnabled(true);
                      toast.info('Hardware scanner mode enabled');
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                      hardwareScannerEnabled 
                        ? 'bg-white shadow text-green-700 border border-green-200' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="hardware-scanner-mode"
                  >
                    <Usb className="w-4 h-4" />
                    <span className="hidden sm:inline">Hardware Scanner</span>
                    <span className="sm:hidden">USB/BT</span>
                    {hardwareScannerEnabled && isScanning && (
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setHardwareScannerEnabled(false);
                      setBarcodeScannerMode('add');
                      setShowBarcodeScanner(true);
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                      !hardwareScannerEnabled 
                        ? 'bg-white shadow text-blue-700 border border-blue-200' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="camera-scanner-mode"
                  >
                    <Camera className="w-4 h-4" />
                    <span className="hidden sm:inline">Camera Scanner</span>
                    <span className="sm:hidden">Camera</span>
                  </button>
                </div>

                {/* Barcode Input */}
                <div className="relative">
                  <Input 
                    ref={barcodeInputRef}
                    placeholder={hardwareScannerEnabled ? "Scan with USB/Bluetooth scanner..." : "Enter barcode manually"} 
                    value={formData.barcode}
                    onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                    className={`pr-20 ${
                      lastHardwareScan === formData.barcode && formData.barcode 
                        ? 'border-green-500 ring-2 ring-green-200 bg-green-50' 
                        : ''
                    }`}
                    data-testid="barcode-input"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {lastHardwareScan === formData.barcode && formData.barcode && (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        <Check className="w-3 h-3 mr-1" />
                        Scanned
                      </Badge>
                    )}
                    {formData.barcode && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFormData({...formData, barcode: ''});
                          setLastHardwareScan(null);
                        }}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Scanner Status */}
                <div className="mt-2 flex items-center gap-2 text-xs">
                  {hardwareScannerEnabled ? (
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${
                      isScanning 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isScanning ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>Scanning...</span>
                        </>
                      ) : (
                        <>
                          <Usb className="w-3 h-3" />
                          <span>Ready for USB/Bluetooth scan</span>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                      <Camera className="w-3 h-3" />
                      <span>Use camera button to scan</span>
                    </div>
                  )}
                  
                  {/* Quick camera scan button when in hardware mode */}
                  {hardwareScannerEnabled && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setBarcodeScannerMode('add');
                        setShowBarcodeScanner(true);
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      <Camera className="w-3 h-3 mr-1" />
                      Use camera instead
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-1">Region</Label>
                <Select 
                  value={formData.region} 
                  onValueChange={(value) => setFormData({...formData, region: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="🌍">🌍 Global</SelectItem>
                    <SelectItem value="🇳🇬">🇳🇬 Nigerian</SelectItem>
                    <SelectItem value="🇬🇭">🇬🇭 Ghanaian</SelectItem>
                    <SelectItem value="🇯🇲">🇯🇲 Jamaican</SelectItem>
                    <SelectItem value="🇹🇹">🇹🇹 Trinidadian</SelectItem>
                    <SelectItem value="🇪🇹">🇪🇹 Ethiopian</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Switch 
                    id="taxable"
                    checked={formData.taxable}
                    onCheckedChange={(checked) => setFormData({...formData, taxable: checked})}
                  />
                  <Label htmlFor="taxable" className="text-sm text-gray-700">
                    <Receipt className="w-4 h-4 inline mr-1" />
                    Taxable (13% HST)
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch 
                    id="inStock"
                    checked={formData.inStock}
                    onCheckedChange={(checked) => setFormData({...formData, inStock: checked})}
                  />
                  <Label htmlFor="inStock" className="text-sm text-gray-700">In Stock</Label>
                </div>
              </div>
              
              {/* Quantity and Reorder Level */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-1">
                    <Package className="w-4 h-4 inline mr-1" />
                    Quantity
                  </Label>
                  <Input 
                    type="number"
                    min="0"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-1">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    Reorder Level
                  </Label>
                  <Input 
                    type="number"
                    min="0"
                    value={formData.reorder_level}
                    onChange={(e) => setFormData({...formData, reorder_level: parseInt(e.target.value) || 4})}
                    className="text-sm"
                  />
                </div>
              </div>
              {formData.quantity < formData.reorder_level && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <span className="text-xs text-yellow-700 font-medium">
                    Warning: Quantity ({formData.quantity}) is below reorder level ({formData.reorder_level})
                  </span>
                </div>
              )}
              
              {/* Update Tracking Fields */}
              <div className="border-t pt-4 mt-2">
                <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Update Tracking
                </h4>
                <div className="space-y-3">
                  {/* AVAILABLE Toggle */}
                  <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <Switch 
                        id="available"
                        checked={formData.available}
                        onCheckedChange={(checked) => setFormData({...formData, available: checked})}
                      />
                      <Label htmlFor="available" className="text-sm font-medium text-gray-700">
                        AVAILABLE
                      </Label>
                    </div>
                    <Badge variant={formData.available ? "default" : "destructive"} className={formData.available ? "bg-green-500" : "bg-red-500"}>
                      {formData.available ? 'Yes - Selling' : 'No - Hidden'}
                    </Badge>
                  </div>
                  {!formData.available && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <span className="text-xs text-red-700 font-medium">
                        This product will NOT be displayed in the shopping area
                      </span>
                    </div>
                  )}
                  
                  {/* UPDATED Toggle */}
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Switch 
                        id="updated"
                        checked={formData.updated}
                        onCheckedChange={(checked) => setFormData({...formData, updated: checked})}
                      />
                      <Label htmlFor="updated" className="text-sm font-medium text-gray-700">
                        UPDATED
                      </Label>
                    </div>
                    <Badge variant={formData.updated ? "default" : "secondary"} className={formData.updated ? "bg-green-500" : ""}>
                      {formData.updated ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  
                  {/* DATE and USER */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="block text-sm font-medium text-gray-700 mb-1">
                        DATE
                      </Label>
                      <Input 
                        type="date"
                        value={formData.updatedDate}
                        onChange={(e) => setFormData({...formData, updatedDate: e.target.value})}
                        className="text-sm"
                        data-testid="product-update-date"
                      />
                    </div>
                    <div>
                      <Label className="block text-sm font-medium text-gray-700 mb-1">
                        USER
                      </Label>
                      <Input 
                        type="text"
                        value={formData.updatedBy}
                        readOnly
                        className="text-sm bg-gray-100"
                        data-testid="product-update-user"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={closeModal}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-blue-500 hover:bg-blue-600"
                onClick={handleSaveProduct}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {editingProduct ? 'Update' : 'Add'} Product
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Enlarged Product Modal */}
      {enlargedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEnlargedProduct(null)} />
          <Card className="relative z-10 w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold text-gray-800">{enlargedProduct.name}</h2>
              <Button variant="ghost" size="sm" onClick={() => setEnlargedProduct(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Large Image */}
              <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden">
                <img 
                  src={enlargedProduct.image} 
                  alt={enlargedProduct.name}
                  className="w-full h-full object-contain"
                />
              </div>
              
              {/* Product Details */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Price</p>
                    <p className="text-2xl font-bold text-green-600">${enlargedProduct.price.toFixed(2)}</p>
                  </div>
                  <div className={`rounded-lg p-3 ${(enlargedProduct.quantity || 10) < 4 ? 'bg-yellow-50' : 'bg-blue-50'}`}>
                    <p className="text-xs text-gray-500">Quantity</p>
                    <p className={`text-2xl font-bold ${(enlargedProduct.quantity || 10) < 4 ? 'text-yellow-600' : 'text-blue-600'}`}>
                      {enlargedProduct.quantity || 10}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Category:</span>
                    <span className="font-medium">{categories.find(c => c.id === enlargedProduct.category)?.emoji} {enlargedProduct.category}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Barcode:</span>
                    <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{enlargedProduct.barcode || 'N/A'}</code>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Region:</span>
                    <span className="font-medium">{enlargedProduct.region}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Taxable:</span>
                    <span className={`font-medium ${enlargedProduct.taxable ? 'text-orange-600' : 'text-gray-600'}`}>
                      {enlargedProduct.taxable ? 'Yes (13% HST)' : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Stock Status:</span>
                    <Badge className={enlargedProduct.inStock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {enlargedProduct.inStock ? 'In Stock' : 'Out of Stock'}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Reorder Level:</span>
                    <span className="font-medium">{enlargedProduct.reorder_level || 4}</span>
                  </div>
                </div>
                
                {(enlargedProduct.quantity || 10) < (enlargedProduct.reorder_level || 4) && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm text-yellow-700 font-medium">
                      Reorder needed! Quantity below threshold.
                    </span>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4">
                  <Button 
                    className="flex-1 bg-blue-500 hover:bg-blue-600"
                    onClick={() => {
                      setEnlargedProduct(null);
                      openEditModal(enlargedProduct);
                    }}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Product
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => {
                      handleDeleteProduct(enlargedProduct.id);
                      setEnlargedProduct(null);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Enlarged Image Preview Modal (for Add/Edit form) */}
      {enlargedImagePreview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80" onClick={() => setEnlargedImagePreview(null)} />
          <div className="relative z-10 max-w-4xl max-h-[90vh] w-full">
            <Button 
              variant="ghost" 
              size="sm" 
              className="absolute top-2 right-2 z-10 bg-white/90 hover:bg-white rounded-full"
              onClick={() => setEnlargedImagePreview(null)}
            >
              <X className="w-6 h-6" />
            </Button>
            <img 
              src={enlargedImagePreview} 
              alt="Enlarged preview" 
              className="w-full h-full object-contain rounded-xl shadow-2xl"
            />
            <p className="text-white text-center mt-4 text-sm">Click anywhere outside to close</p>
          </div>
        </div>
      )}

      {/* Barcode Scanner Modal */}
      <CameraBarcodeScanner
        isOpen={showBarcodeScanner}
        onScan={(barcode) => {
          if (barcodeScannerMode === 'add') {
            setFormData({...formData, barcode});
          } else {
            // Find and edit product by barcode
            const product = products.find(p => p.barcode === barcode);
            if (product) {
              openEditModal(product);
            } else {
              toast.error('Product not found with this barcode');
            }
          }
          setShowBarcodeScanner(false);
        }}
        onClose={() => setShowBarcodeScanner(false)}
        products={products}
        mode={barcodeScannerMode === 'add' ? 'cart' : 'edit'}
      />

      {/* Picture Search Modal */}
      <PictureSearch
        isOpen={showPictureSearch}
        onClose={() => setShowPictureSearch(false)}
        mode="edit"
        onEditProduct={(product) => {
          openEditModal(product);
          setShowPictureSearch(false);
        }}
        products={products}
      />

      {/* Delete All Products Confirmation Modal */}
      {showDeleteAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => {
            if (!deletingAll) {
              setShowDeleteAllModal(false);
              setDeleteConfirmText('');
            }
          }} />
          <Card className="relative z-10 w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">Delete All Products</h2>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 mb-2">
                <strong>Warning:</strong> You are about to delete <strong>{products.length} products</strong> from the database. 
                This will permanently remove all product data including images, prices, and inventory information.
              </p>
              <p className="text-sm text-red-700">
                All caches will be cleared automatically.
              </p>
            </div>
            
            <div className="mb-4">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Type <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">DELETE ALL</span> to confirm:
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="DELETE ALL"
                className="font-mono"
                disabled={deletingAll}
                data-testid="delete-all-confirm-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteAllModal(false);
                  setDeleteConfirmText('');
                }}
                disabled={deletingAll}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAllProducts}
                disabled={deletingAll || deleteConfirmText !== 'DELETE ALL'}
                className="flex-1"
                data-testid="confirm-delete-all-btn"
              >
                {deletingAll ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete All Products
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Import/Restore Products Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => {
            if (!importing) {
              setShowImportModal(false);
              setRestoreFile(null);
            }
          }} />
          <Card className="relative z-10 w-full max-w-lg mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-600" />
              Import / Restore Products
            </h2>
            
            {/* Option 1: Import from Source */}
            <div className="border rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-700 mb-2">Option 1: Import from Website</h3>
              <p className="text-sm text-gray-500 mb-3">
                Import products directly from source website (basic import - may not capture all products due to infinite scroll)
              </p>
              <Input
                value={importSource}
                onChange={(e) => setImportSource(e.target.value)}
                placeholder="https://example.com"
                className="mb-3"
                disabled={importing}
              />
              <Button
                onClick={handleImportFromSource}
                disabled={importing || !importSource}
                className="w-full bg-blue-500 hover:bg-blue-600"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Import from Website
                  </>
                )}
              </Button>
            </div>

            {/* Option 2: Restore from Backup */}
            <div className="border rounded-lg p-4 mb-4 bg-green-50">
              <h3 className="font-semibold text-gray-700 mb-2">Option 2: Restore from Backup (Recommended)</h3>
              <p className="text-sm text-gray-500 mb-3">
                Upload a JSON backup file to restore all products. Use the backup file provided with your deployment.
              </p>
              <input
                type="file"
                accept=".json"
                onChange={(e) => setRestoreFile(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-100 file:text-green-700 hover:file:bg-green-200 mb-3"
                disabled={importing}
              />
              {restoreFile && (
                <p className="text-sm text-green-600 mb-3">Selected: {restoreFile.name}</p>
              )}
              <Button
                onClick={handleRestoreFromFile}
                disabled={importing || !restoreFile}
                className="w-full bg-green-500 hover:bg-green-600"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Restore from Backup
                  </>
                )}
              </Button>
            </div>

            {/* Option 3: Export Current Products */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="font-semibold text-gray-700 mb-2">Export Current Products</h3>
              <p className="text-sm text-gray-500 mb-3">
                Download current products as a backup file
              </p>
              <Button
                onClick={handleExportProducts}
                variant="outline"
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Export to JSON
              </Button>
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportModal(false);
                  setRestoreFile(null);
                }}
                disabled={importing}
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => {
            if (!bulkUploading) {
              setShowBulkUploadModal(false);
              setBulkUploadImages([]);
            }
          }} />
          <Card className="relative z-10 w-full max-w-3xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-500" />
              Bulk Upload Products
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Upload multiple images to create products quickly. You can add names, prices, and other details later.
            </p>
            
            {/* Category Selection */}
            <div className="mb-4 flex flex-wrap gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">Default Category</Label>
                <Select value={bulkUploadCategory} onValueChange={setBulkUploadCategory}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c.id !== 'all').map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.emoji} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* AI Product Name Toggle */}
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex-1">
                  <Label className="text-sm font-medium text-purple-800 flex items-center gap-1">
                    <Sparkles className="w-4 h-4" />
                    AI Product Recognition
                  </Label>
                  <p className="text-xs text-purple-600">Read product name & weight from images</p>
                </div>
                <Switch
                  checked={bulkUploadUseAI}
                  onCheckedChange={setBulkUploadUseAI}
                  className="data-[state=checked]:bg-purple-600"
                />
              </div>
            </div>
            
            {/* Image Preview Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-4">
              {bulkUploadImages.map((item, index) => (
                <div key={index} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                  <img 
                    src={item.preview} 
                    alt={item.filename}
                    className="w-full h-full object-cover"
                  />
                  {/* Status Overlay */}
                  {item.uploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                  {item.uploaded && (
                    <div className="absolute top-1 left-1 bg-green-500 text-white p-1 rounded-full">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                  {item.error && (
                    <div className="absolute inset-0 bg-red-500/70 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-white" />
                    </div>
                  )}
                  {/* Remove Button */}
                  {!bulkUploading && (
                    <button
                      onClick={() => removeBulkImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                  {/* Filename */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
                    <p className="text-xs text-white truncate">{item.filename}</p>
                  </div>
                </div>
              ))}
              
              {/* Add More Button */}
              {!bulkUploading && (
                <button
                  onClick={() => bulkUploadInputRef.current?.click()}
                  className="aspect-square bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:border-purple-400 hover:bg-purple-50 transition-colors"
                >
                  <Plus className="w-6 h-6 text-gray-400" />
                  <span className="text-xs text-gray-500 mt-1">Add More</span>
                </button>
              )}
            </div>
            
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Products will be created with placeholder names and $0.00 price. 
                They will be marked as &quot;unavailable&quot; until you complete their details.
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkUploadModal(false);
                  setBulkUploadImages([]);
                }}
                disabled={bulkUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkUpload}
                disabled={bulkUploading || bulkUploadImages.length === 0}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {bulkUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Create {bulkUploadImages.length} Products
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* CSV Import Preview Modal */}
      {showCsvModal && csvPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => {
            setShowCsvModal(false);
            setCsvPreview(null);
            setCsvErrors([]);
          }} />
          <Card className="relative z-10 w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-500" />
              CSV Import Preview
            </h2>
            
            {csvErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-red-800 mb-1">Errors found:</p>
                <ul className="text-xs text-red-600 list-disc list-inside">
                  {csvErrors.slice(0, 5).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {csvErrors.length > 5 && <li>...and {csvErrors.length - 5} more</li>}
                </ul>
              </div>
            )}
            
            <p className="text-gray-600 mb-4">
              Found <span className="font-bold text-green-600">{csvPreview.length}</span> valid products to import
            </p>
            
            {/* Preview Table */}
            <div className="border rounded-lg overflow-hidden mb-4 max-h-[300px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Price</th>
                    <th className="px-3 py-2 text-left">Category</th>
                    <th className="px-3 py-2 text-left">Taxable</th>
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.slice(0, 20).map((product, idx) => (
                    <tr key={idx} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium">{product.name}</td>
                      <td className="px-3 py-2">${product.price?.toFixed(2)}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-xs">{product.category}</Badge>
                      </td>
                      <td className="px-3 py-2">{product.taxable ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {csvPreview.length > 20 && (
                <p className="text-xs text-center text-gray-500 py-2 bg-gray-50 border-t">
                  ...and {csvPreview.length - 20} more products
                </p>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowCsvModal(false);
                  setCsvPreview(null);
                  setCsvErrors([]);
                }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-green-500 hover:bg-green-600"
                onClick={importCsvProducts}
                disabled={csvUploading || csvPreview.length === 0}
              >
                {csvUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Import {csvPreview.length} Products
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Duplicate Review Modal - Shows ALL duplicates */}
      {showDuplicateModal && duplicateWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => {
            setShowDuplicateModal(false);
            setSelectedDuplicate(null);
          }} />
          <Card className="relative z-10 w-full max-w-5xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Duplicate Product Review
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              We found {(duplicateWarning.name_matches?.length || 0) + (duplicateWarning.image_matches?.length || 0)} potential duplicate(s). 
              Review each one and decide what action to take.
            </p>
            
            {/* New Product Being Added */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-6">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                New Product You&apos;re Adding
              </h3>
              <div className="flex gap-4 items-start">
                {formData.image ? (
                  <img 
                    src={formData.image} 
                    alt="New product" 
                    className="w-24 h-24 object-contain rounded-lg bg-white border flex-shrink-0"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Package className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="font-medium text-gray-800 text-sm">{formData.name || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Price</p>
                    <p className="font-medium text-gray-800 text-sm">${formData.price || '0.00'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Category</p>
                    <p className="font-medium text-gray-800 text-sm">{formData.category || 'None'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Barcode</p>
                    <p className="font-medium text-gray-800 text-sm">{formData.barcode || 'None'}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* All Potential Duplicates */}
            <div className="mb-6">
              <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                All Potential Duplicates ({(duplicateWarning.name_matches?.length || 0) + (duplicateWarning.image_matches?.length || 0)})
              </h3>
              
              <div className="space-y-3">
                {/* Name Matches */}
                {duplicateWarning.name_matches?.map((match, idx) => (
                  <div key={`name-${idx}`} className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <div className="flex gap-4 items-start">
                      {match.product.image ? (
                        <img 
                          src={match.product.image} 
                          alt={match.product.name} 
                          className="w-20 h-20 object-contain rounded-lg bg-white border flex-shrink-0"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Package className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-gray-800">{match.product.name}</span>
                          <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                            {match.similarity}% name match
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <p className="text-xs text-gray-500">Price</p>
                            <p className="font-medium">${match.product.price?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Category</p>
                            <p className="font-medium">{match.product.category || 'None'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Barcode</p>
                            <p className="font-medium">{match.product.barcode || 'None'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Stock</p>
                            <p className={`font-medium ${match.product.inStock ? 'text-green-600' : 'text-red-600'}`}>
                              {match.product.inStock ? 'In Stock' : 'Out of Stock'}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-xs"
                          onClick={() => {
                            setShowDuplicateModal(false);
                            setShowAddModal(false);
                            openEditModal(match.product);
                            setDuplicateWarning(null);
                          }}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="text-xs"
                          onClick={async () => {
                            if (confirm(`Delete "${match.product.name}"?`)) {
                              try {
                                const token = localStorage.getItem('auth_token');
                                const res = await fetch(`${API_URL}/api/products/${match.product.id}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  toast.success('Product deleted');
                                  fetchProducts();
                                  // Update duplicate warning to remove deleted product
                                  setDuplicateWarning(prev => ({
                                    ...prev,
                                    name_matches: prev.name_matches?.filter(m => m.product.id !== match.product.id),
                                    image_matches: prev.image_matches?.filter(m => m.product.id !== match.product.id),
                                    has_duplicates: (prev.name_matches?.length || 0) + (prev.image_matches?.length || 0) > 1
                                  }));
                                }
                              } catch (error) {
                                toast.error('Failed to delete');
                              }
                            }
                          }}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Image Matches */}
                {duplicateWarning.image_matches?.filter(im => 
                  !duplicateWarning.name_matches?.some(nm => nm.product.id === im.product.id)
                ).map((match, idx) => (
                  <div key={`img-${idx}`} className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <div className="flex gap-4 items-start">
                      {match.product.image ? (
                        <img 
                          src={match.product.image} 
                          alt={match.product.name} 
                          className="w-20 h-20 object-contain rounded-lg bg-white border flex-shrink-0"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Package className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-gray-800">{match.product.name}</span>
                          <Badge variant="outline" className="text-purple-600 border-purple-300 text-xs">
                            {match.similarity}% image match
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <p className="text-xs text-gray-500">Price</p>
                            <p className="font-medium">${match.product.price?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Category</p>
                            <p className="font-medium">{match.product.category || 'None'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Barcode</p>
                            <p className="font-medium">{match.product.barcode || 'None'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Stock</p>
                            <p className={`font-medium ${match.product.inStock ? 'text-green-600' : 'text-red-600'}`}>
                              {match.product.inStock ? 'In Stock' : 'Out of Stock'}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-xs"
                          onClick={() => {
                            setShowDuplicateModal(false);
                            setShowAddModal(false);
                            openEditModal(match.product);
                            setDuplicateWarning(null);
                          }}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="text-xs"
                          onClick={async () => {
                            if (confirm(`Delete "${match.product.name}"?`)) {
                              try {
                                const token = localStorage.getItem('auth_token');
                                const res = await fetch(`${API_URL}/api/products/${match.product.id}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  toast.success('Product deleted');
                                  fetchProducts();
                                  // Update duplicate warning to remove deleted product
                                  setDuplicateWarning(prev => ({
                                    ...prev,
                                    name_matches: prev.name_matches?.filter(m => m.product.id !== match.product.id),
                                    image_matches: prev.image_matches?.filter(m => m.product.id !== match.product.id),
                                    has_duplicates: (prev.name_matches?.length || 0) + (prev.image_matches?.length || 0) > 1
                                  }));
                                }
                              } catch (error) {
                                toast.error('Failed to delete');
                              }
                            }
                          }}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateWarning(null);
                  // Proceed with adding new product
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Proceed & Add New Product Anyway
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDuplicateModal(false);
                  setSelectedDuplicate(null);
                }}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel & Go Back
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
